/**
 * Config-as-data — operational thresholds live here, not hard-coded in rules,
 * so an SME can tune them without touching code. Every default carries a short
 * provenance note. Loaders deep-merge partial overrides over these defaults.
 *
 * Epic 9: resolveConfig() layers doctrine-requirement-resolved values over the
 * defaults, so every threshold can be governed from the knowledge base rather
 * than hard-coded here. DEFAULT_CONFIG is the authoritative fallback only.
 */

import type { FreshnessThresholds } from './fact.js';
import type { DoctrineRequirement, RequirementQuery } from './requirements.js';
import { resolveRequirements } from './requirements.js';

export interface DutyConfig {
  /** Water-supply thresholds. */
  water: {
    /** Beyond this hydrant distance, flag limited reticulated supply. */
    hydrantConcernDistanceM: number;
    /** Nominal sustained structural flow expectation (L/min) for sizing. */
    sustainedFlowLpm: number;
    /** Onboard water (L) at/above which a unit counts as bulk water. */
    bulkWaterMinL: number;
    /** Total committed onboard water (L) below which supply looks limited. */
    limitedOnboardL: number;
  };
  /** Capability requirements, often sourced from a DoctrineRequirement. */
  capability: {
    /** Minimum BA operators expected at a structure fire (stand-in doctrine). */
    minBaOperatorsStructureFire: number;
  };
  /** EV / lithium-battery hazard thresholds. */
  ev: {
    /** Minimum separation (m) for a fire-damaged/suspected HV battery from any exposure (stand-in doctrine). */
    storageExclusionDistanceM: number;
  };
  /** How fast different fact categories go stale. */
  freshness: {
    default: FreshnessThresholds;
    /** Weather ages quickly. */
    weather: FreshnessThresholds;
  };
  /** Insight presentation. */
  insights: {
    /**
     * Max cards pinned at the top of the stream before the "Show more" fold.
     * Keeps the default view focused on the highest-priority items.
     */
    maxPinnedCards: number;
    /** Max cards shown when the stream is fully expanded (avoids the giant checklist). */
    maxVisibleCards: number;
  };
}

/**
 * Documented defaults. NOTE: figures marked (stand-in) are placeholders for a
 * POC and MUST be reviewed against authoritative doctrine before real use.
 */
export const DEFAULT_CONFIG: DutyConfig = {
  water: {
    // (stand-in) ~200m is a common reticulated-hydrant planning distance.
    hydrantConcernDistanceM: 200,
    // (stand-in) sizing figure for sustained structural firefighting.
    sustainedFlowLpm: 600,
    // (stand-in) a dedicated bulk-water carrier (>= 4500 L). A Cat 1 tanker
    // (~3000 L) is useful but is not "bulk water" for sustained structural attack.
    bulkWaterMinL: 4500,
    // (stand-in) with a distant hydrant, total committed onboard below this looks
    // limited for sustained structural firefighting.
    limitedOnboardL: 9000,
  },
  capability: {
    // (stand-in) drives the multi-brigade "BA shortfall" insight; replace with
    // the authoritative structure-fire BA requirement.
    minBaOperatorsStructureFire: 8,
  },
  ev: {
    // (stand-in) AFAC-3096 states this figure twice (pp.9-10): a fire-damaged
    // HV battery should be kept at least 15 m from any exposure, on scene and
    // during interim storage. Real, cited doctrine — still POC stand-in status
    // pending this app's own SME-approval workflow.
    storageExclusionDistanceM: 15,
  },
  freshness: {
    default: { ageingAfterSec: 15 * 60, staleAfterSec: 60 * 60 },
    weather: { ageingAfterSec: 30 * 60, staleAfterSec: 2 * 60 * 60 },
  },
  insights: {
    maxPinnedCards: 4,
    maxVisibleCards: 8,
  },
};

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function deepMerge<T>(base: T, override: DeepPartial<T>): T {
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [key, val] of Object.entries(override)) {
    if (val === undefined) continue;
    const current = out[key];
    out[key] =
      isPlainObject(current) && isPlainObject(val)
        ? deepMerge(current, val as DeepPartial<typeof current>)
        : val;
  }
  return out as T;
}

/** Merge partial overrides (e.g. from a JSON config blob) over the defaults. */
export function loadConfig(overrides: DeepPartial<DutyConfig> = {}): DutyConfig {
  return deepMerge(DEFAULT_CONFIG, overrides);
}

/**
 * Maps a RequirementParameterKey to its DutyConfig path so the resolver can
 * produce a config override automatically. This is the only place that knows
 * both the requirements key names and the DutyConfig shape — keep them in sync.
 */
const CONFIG_PARAM_MAP: Record<string, (cfg: DeepPartial<DutyConfig>, v: number) => void> = {
  minBaOperators: (c, v) => {
    c.capability ??= {};
    c.capability.minBaOperatorsStructureFire = v;
  },
  hydrantConcernDistanceM: (c, v) => {
    c.water ??= {};
    c.water.hydrantConcernDistanceM = v;
  },
  sustainedFlowLpm: (c, v) => {
    c.water ??= {};
    c.water.sustainedFlowLpm = v;
  },
  bulkWaterMinL: (c, v) => {
    c.water ??= {};
    c.water.bulkWaterMinL = v;
  },
  evStorageExclusionDistanceM: (c, v) => {
    c.ev ??= {};
    c.ev.storageExclusionDistanceM = v;
  },
  limitedOnboardL: (c, v) => {
    c.water ??= {};
    c.water.limitedOnboardL = v;
  },
};

/**
 * Resolve a DutyConfig from the given doctrine requirements for the operational
 * context described by `query`.
 *
 * Order of precedence (highest wins):
 *   1. `manualOverrides` — explicit runtime/admin overrides
 *   2. Resolved doctrine requirements (most specific applicable, approved only)
 *   3. DEFAULT_CONFIG — always the final fallback
 *
 * The caller should pass the full requirement corpus; this function does the
 * filtering and conflict resolution internally.
 */
export function resolveConfig(
  requirements: DoctrineRequirement[],
  query: RequirementQuery,
  manualOverrides: DeepPartial<DutyConfig> = {},
): DutyConfig {
  const { params } = resolveRequirements(requirements, query);
  const fromRequirements: DeepPartial<DutyConfig> = {};
  for (const [key, resolved] of params) {
    const mapper = CONFIG_PARAM_MAP[key];
    if (mapper) mapper(fromRequirements, resolved.value);
  }
  // Layer: defaults → requirements → manual overrides
  return deepMerge(deepMerge(DEFAULT_CONFIG, fromRequirements), manualOverrides);
}
